import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LocateFixed } from 'lucide-react';
import { apiFetch } from '@/lib/api.js';
import { formatUsdCents, formatNumber } from '@/lib/formatters.js';
import { useParams } from 'react-router-dom';
import { FreshnessBadge } from '../lib/metaFreshness.jsx';
import TrendChart from '../lib/TrendChart.jsx';

// Period selector → windowed account rollup (Meta date_preset). 'compare' shows
// this month vs last with per-metric deltas.
const PERIODS = [
  { key: 'last_7d',    label: 'Last 7D' },
  { key: 'last_30d',   label: 'Last 30D' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'ytd',        label: 'YTD' },
  { key: 'all',        label: 'All time' },
];

// Comparison baselines for the period (applied to tiles + trend + leaderboard).
const COMPARE_MODES = [
  { key: 'none', label: 'No compare' },
  { key: 'prev', label: 'vs prev period' },
  { key: 'yoy',  label: 'vs last year' },
];

// Local YYYY-MM-DD (browser TZ ≈ the account's ET) for Meta time_range windows.
function fmtLocal(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

// Resolve a period key → an explicit window. 'all' stays a preset (Meta
// 'maximum'); the rest are concrete date ranges so they can be shifted to a
// comparison window.
function windowFor(period) {
  const today = new Date();
  const ago = (n) => fmtLocal(addDays(today, -n));
  switch (period) {
    case 'last_7d':    return { since: ago(7), until: ago(1) };
    case 'last_30d':   return { since: ago(30), until: ago(1) };
    case 'this_month': return { since: fmtLocal(new Date(today.getFullYear(), today.getMonth(), 1)), until: fmtLocal(today) };
    case 'last_month': return { since: fmtLocal(new Date(today.getFullYear(), today.getMonth() - 1, 1)), until: fmtLocal(new Date(today.getFullYear(), today.getMonth(), 0)) };
    case 'ytd':        return { since: `${today.getFullYear()}-01-01`, until: fmtLocal(today) };
    case 'all':        return { preset: 'all' };
    default:           return { since: ago(30), until: ago(1) };
  }
}

// Comparison window: 'prev' = the equal-length window immediately before; 'yoy' =
// the same window shifted back 52 weeks (−364d) so weekdays stay aligned.
function shiftWindow(win, mode) {
  if (!win.since || !win.until) return null;
  const s = new Date(`${win.since}T00:00:00`);
  const e = new Date(`${win.until}T00:00:00`);
  if (mode === 'yoy') return { since: fmtLocal(addDays(s, -364)), until: fmtLocal(addDays(e, -364)) };
  if (mode === 'prev') {
    const len = Math.round((e - s) / 86400000) + 1;
    const pe = addDays(s, -1);
    return { since: fmtLocal(addDays(pe, -(len - 1))), until: fmtLocal(pe) };
  }
  return null;
}
function winQuery(win) { return win.preset ? `preset=${win.preset}` : `since=${win.since}&until=${win.until}`; }

// Headline metrics overlaid in compare mode (keeps the chart readable vs 11×2).
const COMPARE_BASE = ['spend', 'value', 'purchases', 'roas'];
function buildCompareStages(cmpLabel) {
  const byKey = Object.fromEntries(TREND_STAGES.map((s) => [s.key, s]));
  const out = [];
  for (const k of COMPARE_BASE) {
    const s = byKey[k];
    out.push(s);
    out.push({ key: `${k}__cmp`, label: `${s.label} · ${cmpLabel}`, className: `${s.className} opacity-40` });
  }
  return out;
}
function mergeCompareSeries(cur, cmp) {
  const n = Math.max(cur.length, cmp.length);
  const out = [];
  for (let i = 0; i < n; i++) {
    const c = cur[i] || {}; const p = cmp[i] || {};
    const row = { day: c.day || p.day };
    for (const k of COMPARE_BASE) { row[k] = c[k] || 0; row[`${k}__cmp`] = p[k] || 0; }
    out.push(row);
  }
  return out;
}

// Campaign-centric Meta performance view. Lists all Meta campaigns
// (from the persisted meta_campaigns + meta_adsets tables) as a sortable
// table. Each campaign carries the server's persisted campaign→event match
// (matched_event {id,date,talent} + match_method — pin → destination URL →
// name date, computed by the meta poller); the Matched tab is a chronological
// timeline by that real event date (today parked at top, like /list).
// Campaigns that resolved to no event live on the Unmatched tab, and can be
// pinned to one manually from the expanded row.

const STATUS_TONE = {
  ACTIVE:    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  PAUSED:    'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  COMPLETED: 'bg-muted text-muted-foreground',
  DELETED:   'bg-destructive/15 text-destructive',
};

// How the server matched the campaign to its event (match_method).
const MATCH_METHOD_BADGE = {
  url:              { label: 'URL',    title: 'Matched by the ad’s destination URL' },
  name_date:        { label: 'Date',   title: 'Matched by the date in the campaign name' },
  name_date_talent: { label: 'Date',   title: 'Matched by name date, disambiguated by talent (two events share this date)' },
  manual:           { label: 'Pinned', title: 'Manually pinned to this event' },
  general:          { label: 'General', title: 'Brand campaign (listing-page destination or "general" in the name). Never matched to a single event.' },
};

// Trend-chart series. One shared Y axis, so default-show only the two $-scale
// lines (Spend/Value); everything else is toggleable (very different scales —
// view them one at a time via the chips).
const TREND_STAGES = [
  { key: 'spend',       label: 'Spend ($)',      className: 'text-emerald-500' },
  { key: 'value',       label: 'Value ($)',      className: 'text-sky-500' },
  { key: 'purchases',   label: 'Purchases',      className: 'text-violet-500' },
  { key: 'roas',        label: 'ROAS',           className: 'text-amber-500' },
  { key: 'impressions', label: 'Impressions',    className: 'text-rose-500' },
  { key: 'reach',       label: 'Reach',          className: 'text-teal-500' },
  { key: 'clicks',      label: 'Clicks',         className: 'text-cyan-500' },
  { key: 'ctr',         label: 'CTR (%)',        className: 'text-fuchsia-500' },
  { key: 'cpm',         label: 'CPM ($)',        className: 'text-orange-500' },
  { key: 'cpc',         label: 'CPC ($)',        className: 'text-lime-500' },
  { key: 'cpp',         label: 'Cost/Purch ($)', className: 'text-indigo-500' },
];
// All series except the two shown by default.
const TREND_DEFAULT_HIDDEN = TREND_STAGES.map((s) => s.key).filter((k) => k !== 'spend' && k !== 'value');

function dollars(c)   { return formatUsdCents(c); }       // "$824,497" (grouped, no cents)
function num(n)       { return formatNumber(n); }         // "40,506,656"
function deltaPct(cur, prev) {
  if (prev == null || prev === 0) return null;
  return ((cur - prev) / Math.abs(prev)) * 100;
}
function pct(n)       { return `${(n || 0).toFixed(2)}%`; }
function roasFmt(n)   { return n > 0 ? `${n.toFixed(2)}x` : '—'; }
function roasTone(n) {
  if (n >= 4) return 'text-emerald-600 dark:text-emerald-400';
  if (n >= 2) return 'text-amber-600 dark:text-amber-400';
  if (n > 0)  return 'text-destructive';
  return 'text-muted-foreground';
}
// Muted "synced Nd ago" hint for rows whose snapshot is stale (>7 days). Paused
// campaigns refresh in the background, so figures can lag — surface that rather
// than letting an old $0/snapshot read as a bug.
function staleHint(lastSyncedAt) {
  if (!lastSyncedAt) return null;
  const days = Math.floor((Date.now() - new Date(lastSyncedAt).getTime()) / 86400000);
  return days > 7 ? `synced ${days}d ago` : null;
}
// Event dates are pure calendar dates — format in UTC so they don't shift a day.
function fmtEventDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit', timeZone: 'UTC' });
}

export default function MetaPerformance() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('matched'); // 'matched' | 'unmatched'
  const [sortKey, setSortKey] = useState('event');
  const [sortDir, setSortDir] = useState('asc');
  const [expanded, setExpanded] = useState(new Set());
  const [filter, setFilter] = useState('');
  const listWrapRef = useRef(null);
  const [listH, setListH] = useState(0);
  // Windowed rollup driven by the period selector (separate, cheap fetch).
  const [period, setPeriod] = useState('last_30d');
  const [compareMode, setCompareMode] = useState('none'); // 'none' | 'prev' | 'yoy'
  const [roll, setRoll] = useState(null);       // current period's account rollup
  const [rollCmp, setRollCmp] = useState(null); // { cur, prev } when comparing
  const [rollErr, setRollErr] = useState(null);
  const [series, setSeries] = useState(null);   // daily trend for the selected period
  const [seriesCmp, setSeriesCmp] = useState(null); // comparison-window daily series
  const [seriesErr, setSeriesErr] = useState(null);
  const [winCampaigns, setWinCampaigns] = useState(null); // per-campaign metrics for the selected window (table + leaderboard)
  const [winErr, setWinErr] = useState(null);
  const [manageOpen, setManageOpen] = useState(false); // custom match-groups modal
  const [refreshKey, setRefreshKey] = useState(0); // bumped by the auto-refresh timer
  // Track each panel's "real" inputs so a background refresh (refreshKey bump)
  // updates in place — only a genuine period/compare change shows "Loading…".
  const rollKeyRef = useRef('');
  const seriesKeyRef = useRef('');
  const topKeyRef = useRef('');
  // 'all' has no clean comparison window; force compare off there.
  const cmp = period === 'all' ? 'none' : compareMode;

  // Always load the full set — the chronological scroll + tabs + filter replace
  // the old window buttons (and a wide range is needed for "scroll up = past").
  const load = useCallback((quiet) => {
    if (!quiet) { setData(null); setError(null); }
    return apiFetch(`/api/tenants/${tenantSlug}/meta-performance?window=all`)
      .then((r) => r.json().then((b) => ({ ok: r.ok, body: b })))
      .then(({ ok, body }) => {
        if (!ok) {
          setError(
            body.error === 'cache_warming'   ? 'Meta poller cache is warming. Wait ~30s and reload.' :
            body.error === 'meta_creds_missing' ? 'Marketing API token not configured.' :
            body.detail || body.error || 'fetch failed'
          );
          return;
        }
        setData(body);
      })
      .catch((e) => setError(e.message));
  }, [tenantSlug]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh while the tab is visible — the backend poller updates the DB
  // every ~10 min; keep the page live without a manual reload. Quiet refresh
  // (no table flicker); the windowed fetches re-pull via refreshKey. Skipped
  // when hidden so background tabs don't poll.
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      load(true);
      setRefreshKey((k) => k + 1);
    }, 60000);
    return () => clearInterval(id);
  }, [load]);

  // Windowed account rollup + (when comparing) the comparison window, for deltas.
  // 'all' uses the DB-summed lifetime rollup (data.rollup) — Meta's `maximum`
  // preset returns 0 purchases for the full range.
  useEffect(() => {
    let cancel = false;
    const primary = `${tenantSlug}|${period}|${cmp}`;
    const fresh = rollKeyRef.current !== primary; rollKeyRef.current = primary;
    setRollErr(null);
    if (period === 'all') { if (fresh) { setRoll(null); setRollCmp(null); } return undefined; }
    const win = windowFor(period);
    const get = (w) => apiFetch(`/api/tenants/${tenantSlug}/meta-performance/rollup?${winQuery(w)}`)
      .then((r) => r.json())
      .then((b) => { if (b.error) throw new Error(b.detail || b.error); return b.rollup || {}; });
    const cmpWin = cmp !== 'none' ? shiftWindow(win, cmp) : null;
    if (cmpWin) {
      Promise.all([get(win), get(cmpWin)])
        .then(([a, b]) => { if (!cancel) { setRoll(a); setRollCmp({ cur: a, prev: b }); } })
        .catch((e) => { if (!cancel) setRollErr(e.message); });
    } else {
      setRollCmp(null);
      get(win).then((a) => { if (!cancel) setRoll(a); }).catch((e) => { if (!cancel) setRollErr(e.message); });
    }
    return () => { cancel = true; };
  }, [tenantSlug, period, cmp, refreshKey]);

  // Daily trend series (+ comparison window when comparing).
  useEffect(() => {
    let cancel = false;
    const primary = `${tenantSlug}|${period}|${cmp}`;
    const fresh = seriesKeyRef.current !== primary; seriesKeyRef.current = primary;
    if (fresh) { setSeries(null); setSeriesCmp(null); }
    setSeriesErr(null);
    const win = windowFor(period);
    const get = (w) => apiFetch(`/api/tenants/${tenantSlug}/meta-performance/timeseries?${winQuery(w)}`)
      .then((r) => r.json())
      .then((b) => { if (b.error) throw new Error(b.detail || b.error); return b.series || []; });
    const cmpWin = cmp !== 'none' ? shiftWindow(win, cmp) : null;
    Promise.all([get(win), cmpWin ? get(cmpWin) : Promise.resolve(null)])
      .then(([a, b]) => { if (!cancel) { setSeries(a); setSeriesCmp(b); } })
      .catch((e) => { if (!cancel) setSeriesErr(e.message); });
    return () => { cancel = true; };
  }, [tenantSlug, period, cmp, refreshKey]);

  // Per-campaign metrics for the window — scopes BOTH the campaign table and
  // the ROAS leaderboard to the time filter. Skipped for 'all' (lifetime view).
  // No per-campaign comparison: campaigns are event-based (each event is its
  // own campaign), so a vs-last-period/year delta would just read "new".
  useEffect(() => {
    let cancel = false;
    const primary = `${tenantSlug}|${period}`;
    const fresh = topKeyRef.current !== primary; topKeyRef.current = primary;
    if (fresh) setWinCampaigns(null);
    setWinErr(null);
    if (period === 'all') { setWinCampaigns(null); return undefined; }
    apiFetch(`/api/tenants/${tenantSlug}/meta-performance/top-campaigns?${winQuery(windowFor(period))}`)
      .then((r) => r.json())
      .then((b) => { if (cancel) return; if (b.error) throw new Error(b.detail || b.error); setWinCampaigns(b.campaigns || []); })
      .catch((e) => { if (!cancel) setWinErr(e.message); });
    return () => { cancel = true; };
  }, [tenantSlug, period, refreshKey]);

  // Split campaigns by whether their ad URL resolved to a real event.
  const lifetimeCampaigns = data?.campaigns || [];
  const windowed = period !== 'all';
  // Table source: 'all' = every campaign with lifetime totals; a window = only
  // campaigns that delivered in it, with the WINDOW's metrics as totals (the
  // campaign's metadata — matched event, groups, status, destination, schedule —
  // still comes from the lifetime row). Null while the windowed fetch is in
  // flight on a fresh period (table shows a loading row, not a wrong list).
  const allCampaigns = useMemo(() => {
    if (!windowed) return lifetimeCampaigns;
    if (!winCampaigns) return null;
    const winById = new Map(winCampaigns.map((w) => [w.id, w]));
    return lifetimeCampaigns
      .filter((c) => winById.has(c.id))
      .map((c) => ({ ...c, totals: { ...winById.get(c.id) } }));
  }, [lifetimeCampaigns, winCampaigns, windowed]) || [];
  const windowReady = !windowed || !!winCampaigns;
  const groups = data?.groups || [];
  // Matched = resolved to a real event AND not captured by a custom group, so a
  // grouped campaign (e.g. Tixr) shows only under its group tab — no overlap.
  const isMatchedOnly = (c) => c.matched_event && (c.matched_groups || []).length === 0;
  const matchedCount = useMemo(() => allCampaigns.filter(isMatchedOnly).length, [allCampaigns]);
  const groupCounts = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = 0;
    for (const c of allCampaigns) for (const gid of (c.matched_groups || [])) if (gid in m) m[gid] += 1;
    return m;
  }, [allCampaigns, groups]);
  // Unmatched excludes campaigns captured by a custom group — groups act as "custom matches".
  const unmatchedCount = useMemo(
    () => allCampaigns.filter((c) => !c.matched_event && (c.matched_groups || []).length === 0).length,
    [allCampaigns],
  );
  const activeList = useMemo(() => {
    if (tab === 'matched') return allCampaigns.filter(isMatchedOnly);
    if (tab === 'unmatched') return allCampaigns.filter((c) => !c.matched_event && (c.matched_groups || []).length === 0);
    return allCampaigns.filter((c) => (c.matched_groups || []).includes(tab)); // tab === group id
  }, [allCampaigns, tab]);
  // Group tab → summed rollup of its campaigns' lifetime totals (additive metrics).
  const groupRollup = useMemo(() => {
    if (tab === 'matched' || tab === 'unmatched') return null;
    let spend_cents = 0, purchases = 0, purchase_value_cents = 0;
    for (const c of activeList) {
      spend_cents += c.totals?.spend_cents || 0;
      purchases += c.totals?.purchases || 0;
      purchase_value_cents += c.totals?.purchase_value_cents || 0;
    }
    return { count: activeList.length, spend_cents, purchases, purchase_value_cents, roas: spend_cents > 0 ? purchase_value_cents / spend_cents : 0 };
  }, [activeList, tab]);

  const sorted = useMemo(() => {
    const filtered = filter
      ? activeList.filter((c) => c.name?.toLowerCase().includes(filter.toLowerCase()))
      : activeList;
    const sign = sortDir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'name':      av = a.name || ''; bv = b.name || ''; return sign * av.localeCompare(bv);
        case 'status':    av = a.effective_status || ''; bv = b.effective_status || ''; return sign * av.localeCompare(bv);
        case 'event':     av = a.matched_event?.date || ''; bv = b.matched_event?.date || ''; return sign * av.localeCompare(bv);
        case 'spend':     av = a.totals.spend_cents; bv = b.totals.spend_cents; break;
        case 'purchases': av = a.totals.purchases; bv = b.totals.purchases; break;
        case 'value':     av = a.totals.purchase_value_cents; bv = b.totals.purchase_value_cents; break;
        case 'roas':      av = a.totals.roas; bv = b.totals.roas; break;
        default:          av = 0; bv = 0;
      }
      return sign * (av - bv);
    });
  }, [activeList, sortKey, sortDir, filter]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
    // Event defaults to asc so re-selecting it re-arms the chronological "today" anchor.
    else { setSortKey(key); setSortDir(key === 'event' ? 'asc' : 'desc'); }
  };
  // Switching tabs resets to that tab's natural ordering: Matched = chronological
  // by event date; Unmatched = spend desc (no event date to sort on).
  const switchTab = (t) => {
    setTab(t);
    if (t === 'matched') { setSortKey('event'); setSortDir('asc'); }
    else { setSortKey('spend'); setSortDir('desc'); }
  };
  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  // Activate / pause a campaign on Meta — confirmed via an in-UI dialog (no
  // window.confirm/alert), then update the row in place.
  const [statusBusy, setStatusBusy] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null); // { c, next }
  const [statusErr, setStatusErr] = useState(null);
  const requestStatus = (c, next) => { setStatusErr(null); setPendingStatus({ c, next }); };
  const confirmStatus = async () => {
    if (!pendingStatus) return;
    const { c, next } = pendingStatus;
    setStatusBusy(c.id);
    setStatusErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/meta-campaigns/${c.id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      const d = await res.json();
      if (!res.ok) {
        setStatusErr(d.error === 'read_only_mode' ? 'Writes are disabled (READ_ONLY_MODE).' : (d.detail || d.error || 'failed'));
        return;
      }
      setData((prev) => prev && ({
        ...prev,
        campaigns: prev.campaigns.map((x) => (x.id === c.id ? { ...x, effective_status: next } : x)),
      }));
      setPendingStatus(null);
    } catch (e) {
      setStatusErr(e.message);
    } finally {
      setStatusBusy(null);
    }
  };
  // Trailing blank rows so the first-upcoming ("today") row can sit flush at the
  // top of the scroll box even when few campaigns follow it — and so a short
  // list still fills the frame. Only in the chronological Matched view.
  const fillerCount = (rowH) => (chronological && firstUpcomingIdx >= 0 && listH)
    ? Math.max(0, Math.ceil(listH / rowH) - (sorted.length - firstUpcomingIdx) + 1)
    : 0;

  // Chronological default (Matched tab, Event ↑): campaigns for past events sit
  // above, the first current/upcoming event is parked at the top of the scroll
  // box, later events below. Re-sorting by any column opts out of the anchor.
  const todayStr = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD
  const chronological = tab === 'matched' && sortKey === 'event' && sortDir === 'asc';
  const firstUpcomingIdx = useMemo(() => (
    chronological ? sorted.findIndex((c) => (c.matched_event?.date || '') >= todayStr) : -1
  ), [sorted, chronological, todayStr]);

  // Park the scroll box on the first current/upcoming campaign: past above
  // (scroll up), newer below (scroll down). No upcoming → show the bottom.
  // Lifted verbatim from the List view so behaviour matches /list.
  const findScroll = () => Array.from(document.querySelectorAll('[data-timeline-scroll]')).find((n) => n.offsetParent !== null);
  const homeDelta = (c) => {
    const r = c.querySelector('[data-first-upcoming]');
    if (!r) return c.scrollHeight - c.clientHeight - c.scrollTop; // no upcoming → bottom
    const head = c.querySelector('thead, [data-sticky-head]');
    return r.getBoundingClientRect().top - c.getBoundingClientRect().top - (head ? head.offsetHeight : 0);
  };
  // "Jump to today" button: re-park on the first upcoming row. Shown once the
  // user has scrolled away from that home position.
  const [showJump, setShowJump] = useState(false);
  const goHome = (smooth) => {
    const c = findScroll();
    if (!c) return;
    c.scrollBy({ top: homeDelta(c), behavior: smooth ? 'smooth' : 'auto' });
    setShowJump(false);
  };
  const onListScroll = (e) => {
    if (!chronological) { setShowJump(false); return; }
    setShowJump(Math.abs(homeDelta(e.currentTarget)) > 64);
  };

  const didAnchor = useRef(false);
  // Re-arm the anchor on return to the chronological default, when the frame
  // height changes (filler rows are added once listH is known → re-park today),
  // and when the windowed rows land (windowReady flips false→true after the
  // period fetch — anchoring before that wastes the shot on an empty table).
  useLayoutEffect(() => { if (chronological) didAnchor.current = false; }, [chronological, listH, windowReady]);
  useLayoutEffect(() => {
    if (!data || !windowReady || sorted.length === 0 || !chronological || didAnchor.current) return;
    if (!findScroll()) return;
    goHome(false);
    didAnchor.current = true;
    setTimeout(() => goHome(false), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, chronological, sorted.length, listH]);

  // Size the list frame to fill the viewport down to the bottom, measured from
  // the frame's top so the header/rollup/filter height is accounted for; the
  // chrome above stays fixed and only the campaign list scrolls. Matches the
  // List view's bounded-scroll pattern. Recomputed on resize and once data
  // renders the box (the ref is null during the loading return).
  useLayoutEffect(() => {
    const calc = () => {
      const el = listWrapRef.current;
      if (!el) return;
      setListH(Math.max(260, Math.round(window.innerHeight - el.getBoundingClientRect().top - 16)));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [data, filter]);

  if (error) return <div className="text-destructive">{error}</div>;
  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  // Windowed rollup drives the tiles. In compare mode, current = this month and
  // each tile shows a delta vs last month.
  const r = period === 'all' ? (data.rollup || {}) : (rollCmp ? rollCmp.cur : (roll || {}));
  const periodLabel = PERIODS.find((p) => p.key === period)?.label || '';
  const cmpLabel = cmp === 'yoy' ? 'last yr' : 'prev';
  const subFor = (key) => {
    if (!rollCmp) return null;
    const d = deltaPct(rollCmp.cur?.[key] || 0, rollCmp.prev?.[key] || 0);
    if (d == null) return <span className="text-muted-foreground">vs —</span>;
    const up = d >= 0;
    return (
      <span className={up ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}>
        {up ? '▲' : '▼'} {Math.abs(d).toFixed(0)}% vs {cmpLabel}
      </span>
    );
  };

  // Compare-aware chart inputs: overlay current vs comparison (headline metrics
  // only) when comparing, else the full multi-metric trend.
  const comparing = cmp !== 'none';
  const chartStages = comparing ? buildCompareStages(cmpLabel) : TREND_STAGES;
  const chartHidden = comparing
    ? chartStages.map((s) => s.key).filter((k) => k !== 'spend' && k !== 'spend__cmp')
    : TREND_DEFAULT_HIDDEN;
  const chartSeries = (comparing && series && seriesCmp) ? mergeCompareSeries(series, seriesCmp) : series;
  // Leaderboard source: the windowed per-campaign metrics, or lifetime totals
  // flattened to the same shape when 'All time' is selected (windowed fetch skipped).
  const leaderboardCampaigns = windowed
    ? winCampaigns
    : lifetimeCampaigns.map((c) => ({ id: c.id, name: c.name, spend_cents: c.totals.spend_cents, roas: c.totals.roas }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Meta performance</h1>
        <div className="flex items-center gap-3">
          <FreshnessBadge cacheMeta={data.cache_meta} />
          <div className="text-sm text-muted-foreground">{sorted.length} of {data.count}</div>
        </div>
      </div>

      {/* Period selector, windowed account rollup (Meta date_preset). */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex max-w-full overflow-x-auto border border-input rounded-md text-xs">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 shrink-0 whitespace-nowrap ${period === p.key ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent/50'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {/* Comparison baseline, applies to tiles, trend, and leaderboard. */}
        <div className="inline-flex max-w-full overflow-x-auto border border-input rounded-md text-xs">
          {COMPARE_MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setCompareMode(m.key)}
              disabled={period === 'all' && m.key !== 'none'}
              className={`px-2.5 py-1 shrink-0 whitespace-nowrap ${cmp === m.key ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent/50'} disabled:opacity-40`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">{periodLabel}</span>
        {rollErr && <span className="text-xs text-destructive">rollup: {rollErr}</span>}
      </div>

      {/* Rollup strip, account-level totals for the selected period. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        <RollupStat label="Spend"     value={dollars(r.spend_cents)} sub={subFor('spend_cents')} />
        <RollupStat label="Impr"      value={num(r.impressions)} sub={subFor('impressions')} />
        <RollupStat label="Reach"     value={num(r.reach)} sub={subFor('reach')} />
        <RollupStat label="Purchases" value={num(r.purchases)} sub={subFor('purchases')} />
        <RollupStat label="Value"     value={dollars(r.purchase_value_cents)} sub={subFor('purchase_value_cents')} />
        <RollupStat label="ROAS"      value={roasFmt(r.roas)} toneClass={roasTone(r.roas)} sub={subFor('roas')} />
      </div>

      {/* Performance graphs, daily trend + top campaigns. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="lg:col-span-2 border border-border rounded-md p-3 bg-card">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Daily trend · {periodLabel}{comparing ? ` · vs ${cmpLabel}` : ''}
          </div>
          {seriesErr ? (
            <div className="text-xs text-destructive py-8 text-center">trend: {seriesErr}</div>
          ) : !chartSeries ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : chartSeries.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No daily data for this period.</div>
          ) : (
            <TrendChart series={chartSeries} stages={chartStages} defaultHidden={chartHidden} height={200} />
          )}
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Top campaigns by ROAS · {periodLabel}
          </div>
          {winErr ? (
            <div className="text-xs text-destructive py-8 text-center">{winErr}</div>
          ) : !leaderboardCampaigns ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : (
            <TopCampaignsBar campaigns={leaderboardCampaigns} filter={filter} />
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="inline-flex max-w-full overflow-x-auto border border-input rounded-md">
          <TabButton active={tab === 'matched'} onClick={() => switchTab('matched')}>Matched · {matchedCount}</TabButton>
          {groups.map((g) => (
            <TabButton key={g.id} active={tab === g.id} onClick={() => switchTab(g.id)}>{g.name} · {groupCounts[g.id] || 0}</TabButton>
          ))}
          <TabButton active={tab === 'unmatched'} onClick={() => switchTab('unmatched')}>Unmatched · {unmatchedCount}</TabButton>
        </div>
        <button
          onClick={() => setManageOpen(true)}
          className="px-2 py-1.5 text-xs rounded-md border border-input hover:bg-accent text-muted-foreground flex-shrink-0"
        >Manage groups</button>
        <input
          type="text"
          placeholder="Filter by campaign name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 min-w-[160px] px-3 py-1.5 text-sm rounded-md border border-input bg-background"
        />
      </div>

      {groupRollup && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <RollupStat label="Campaigns" value={num(groupRollup.count)} />
          <RollupStat label="Spend"     value={dollars(groupRollup.spend_cents)} />
          <RollupStat label="Purchases" value={num(groupRollup.purchases)} />
          <RollupStat label="Value"     value={dollars(groupRollup.purchase_value_cents)} />
          <RollupStat label="ROAS"      value={roasFmt(groupRollup.roas)} toneClass={roasTone(groupRollup.roas)} />
        </div>
      )}

      {manageOpen && (
        <MatchGroupsModal
          tenantSlug={tenantSlug}
          groups={groups}
          onClose={() => setManageOpen(false)}
          onChanged={() => load(true)}
        />
      )}

      <div ref={listWrapRef} className="relative">
      {/* One responsive table for all widths: columns reveal progressively
          (mobile = Campaign/Spend/ROAS, sm adds Event, md adds Status/Purch/Value). */}
      <div
        data-timeline-scroll
        onScroll={onListScroll}
        className="border border-border rounded-md overflow-auto relative"
        style={{ height: listH ? `${listH}px` : '65vh' }}
      >
        <table className="w-full text-sm">
          <thead className="bg-muted text-left sticky top-0 z-10">
            <tr>
              <SortableTh sortKey="name"      label="Campaign"   currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-2/5" />
              <SortableTh sortKey="status"    label="Status"     currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="hidden md:table-cell" />
              {tab === 'matched'
                ? <SortableTh sortKey="event" label="Event"       currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="hidden sm:table-cell" />
                : <th className="px-3 py-2 font-medium hidden sm:table-cell">Destination</th>}
              <SortableTh sortKey="spend"     label="Spend"      currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
              <SortableTh sortKey="purchases" label="Purch"      currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="hidden md:table-cell" />
              <SortableTh sortKey="value"     label="Value"      currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" className="hidden md:table-cell" />
              <SortableTh sortKey="roas"      label="ROAS"       currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
              <th className="px-2 py-2 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {!windowReady ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Loading {periodLabel}…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No campaigns match.</td></tr>
            ) : sorted.map((c, idx) => {
              const isOpen = expanded.has(c.id);
              const isFirstUpcoming = idx === firstUpcomingIdx;
              const t = c.totals;
              const canToggle = c.effective_status === 'ACTIVE' || c.effective_status === 'PAUSED';
              return (
                <Fragment key={c.id}>
                  <tr
                    data-first-upcoming={isFirstUpcoming || undefined}
                    className={`hover:bg-accent/30 cursor-pointer ${isFirstUpcoming ? 'border-t-2 border-primary/50' : 'border-t border-border'}`}
                    onClick={() => toggleExpand(c.id)}
                  >
                    <td className="px-3 py-2 min-w-0">
                      <div className="font-medium">{c.name}</div>
                      {scheduleBadge(c.schedule) && <div className="text-[10px] text-primary/80">{scheduleBadge(c.schedule)}</div>}
                      {staleHint(c.last_synced_at) && <div className="text-[10px] text-muted-foreground" title="Paused campaigns refresh in the background; figures are from the last sync.">{staleHint(c.last_synced_at)}</div>}
                      {/* Status column is hidden below md, surface the badge + action here on mobile/sm. */}
                      <div className="mt-1 flex items-center gap-1.5 md:hidden">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_TONE[c.effective_status] || 'bg-muted text-muted-foreground'}`}>
                          {c.effective_status}
                        </span>
                        {canToggle && (
                          <button
                            onClick={(e) => { e.stopPropagation(); requestStatus(c, c.effective_status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'); }}
                            disabled={statusBusy === c.id}
                            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50"
                          >{statusBusy === c.id ? '…' : (c.effective_status === 'ACTIVE' ? 'Pause' : 'Activate')}</button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_TONE[c.effective_status] || 'bg-muted text-muted-foreground'}`}>
                          {c.effective_status}
                        </span>
                        {canToggle && (
                          <button
                            onClick={(e) => { e.stopPropagation(); requestStatus(c, c.effective_status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'); }}
                            disabled={statusBusy === c.id}
                            className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent disabled:opacity-50"
                            title={c.effective_status === 'ACTIVE' ? 'Pause this campaign on Meta' : 'Activate this campaign on Meta'}
                          >{statusBusy === c.id ? '…' : (c.effective_status === 'ACTIVE' ? 'Pause' : 'Activate')}</button>
                        )}
                      </div>
                    </td>
                    {c.matched_event ? (
                      <td className="px-3 py-2 text-xs text-muted-foreground hidden sm:table-cell">
                        <div className="flex items-center gap-1 max-w-[14rem] min-w-0">
                          <span className="whitespace-nowrap">{fmtEventDate(c.matched_event.date)}</span>
                          {c.matched_event.talent ? <span className="truncate text-foreground">· {c.matched_event.talent}</span> : null}
                          {MATCH_METHOD_BADGE[c.match_method] && (
                            <span
                              className="text-[9px] px-1 py-px rounded bg-muted text-muted-foreground flex-shrink-0"
                              title={MATCH_METHOD_BADGE[c.match_method].title}
                            >{MATCH_METHOD_BADGE[c.match_method].label}</span>
                          )}
                        </div>
                      </td>
                    ) : (
                      <td className="px-3 py-2 text-xs text-muted-foreground hidden sm:table-cell" title={c.destination_url || ''}>
                        <div className="flex items-center gap-1 max-w-[16rem] min-w-0">
                          {c.match_disabled ? (
                            <span className="text-[9px] px-1 py-px rounded bg-muted text-muted-foreground flex-shrink-0" title="Manually excluded from event matching">Off</span>
                          ) : MATCH_METHOD_BADGE[c.match_method] ? (
                            <span className="text-[9px] px-1 py-px rounded bg-muted text-muted-foreground flex-shrink-0" title={MATCH_METHOD_BADGE[c.match_method].title}>{MATCH_METHOD_BADGE[c.match_method].label}</span>
                          ) : null}
                          <span className="truncate">{c.destination_url || '—'}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2 text-right tabular-nums">{dollars(t.spend_cents)}</td>
                    <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">{num(t.purchases)}</td>
                    <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">{dollars(t.purchase_value_cents)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-medium ${roasTone(t.roas)}`}>{roasFmt(t.roas)}</td>
                    <td className="px-2 py-2 text-muted-foreground">{isOpen ? '▾' : '▸'}</td>
                  </tr>
                  {isOpen && (
                    <tr key={`${c.id}-detail`} className="bg-muted/20 border-t border-border">
                      <td colSpan={8} className="px-4 py-3">
                        <CampaignDetail campaign={c} tenantSlug={tenantSlug} windowed={windowed} onSaved={() => load(true)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {Array.from({ length: fillerCount(41) }).map((_, i) => (
              <tr key={`blank-${i}`} aria-hidden="true">
                <td colSpan={8} className="border-t border-border/40 h-[41px]"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {chronological && showJump && (
        <button
          onClick={() => goHome(true)}
          title="Jump back to today"
          className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg hover:opacity-90"
        >
          <LocateFixed size={14} /> Today
        </button>
      )}
      </div>

      {pendingStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => !statusBusy && setPendingStatus(null)} />
          <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-sm p-4">
            <h2 className="font-semibold mb-2">{pendingStatus.next === 'PAUSED' ? 'Pause' : 'Activate'} campaign</h2>
            <p className="text-sm text-muted-foreground mb-3">
              {pendingStatus.next === 'PAUSED' ? 'Pause' : 'Activate'}{' '}
              <span className="font-medium text-foreground">{pendingStatus.c.name}</span> on Meta?
            </p>
            {statusErr && <div className="text-xs text-destructive mb-2 break-words">{statusErr}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setPendingStatus(null)} disabled={!!statusBusy}
                className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">Cancel</button>
              <button onClick={confirmStatus} disabled={!!statusBusy}
                className={`px-3 py-1.5 text-sm rounded-md disabled:opacity-50 ${pendingStatus.next === 'PAUSED' ? 'bg-amber-600 text-white hover:opacity-90' : 'bg-primary text-primary-foreground hover:opacity-90'}`}>
                {statusBusy ? 'Working…' : (pendingStatus.next === 'PAUSED' ? 'Pause' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm shrink-0 whitespace-nowrap border-r border-input last:border-r-0 ${active ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/40'}`}
    >{children}</button>
  );
}

function SortableTh({ sortKey, label, currentKey, dir, onClick, align = 'left', className = '' }) {
  const active = sortKey === currentKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-3 py-2 font-medium cursor-pointer hover:bg-accent/40 text-${align} ${className}`}
    >
      {label}
      {active && <span className="ml-1 text-xs text-muted-foreground">{dir === 'desc' ? '↓' : '↑'}</span>}
    </th>
  );
}

function RollupStat({ label, value, toneClass, sub }) {
  return (
    <div className="border border-border rounded-md px-3 py-2 bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${toneClass || ''}`}>{value}</div>
      {sub && <div className="text-[10px] tabular-nums mt-0.5">{sub}</div>}
    </div>
  );
}

// Leaderboard of the best-performing campaigns by ROAS for the selected window
// (windowed per-campaign metrics — follows the time filter). Gated to a minimum
// spend so a tiny-spend fluke (e.g. $5 → 40x) doesn't top the list. Bar ∝ ROAS.
// Items are flat: { id, name, spend_cents, roas }.
const TOP_ROAS_MIN_SPEND_CENTS = 10000; // $100
function TopCampaignsBar({ campaigns, filter }) {
  const top = useMemo(() => {
    const q = (filter || '').toLowerCase();
    return [...(campaigns || [])]
      .filter((c) => (c.spend_cents || 0) >= TOP_ROAS_MIN_SPEND_CENTS && (c.roas || 0) > 0)
      .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
      .sort((a, b) => b.roas - a.roas)
      .slice(0, 8);
  }, [campaigns, filter]);
  if (!top.length) return <div className="text-sm text-muted-foreground py-8 text-center">No ROAS data for this period.</div>;
  const max = top[0].roas || 1;
  return (
    <div className="space-y-1.5">
      {top.map((c) => (
        <div key={c.id} className="text-xs">
          <div className="flex justify-between gap-2">
            <span className="truncate" title={c.name}>{c.name}</span>
            <span className="tabular-nums flex-shrink-0">
              <span className={`font-medium ${roasTone(c.roas)}`}>{roasFmt(c.roas)}</span>
              <span className="text-muted-foreground"> · {dollars(c.spend_cents)}</span>
            </span>
          </div>
          <div className="mt-0.5 h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary/60 rounded-full" style={{ width: `${Math.max(2, (c.roas / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const DOW_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const hhmm = (t) => (t ? String(t).slice(0, 5) : ''); // 'HH:MM:SS' → 'HH:MM'
function ampm(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const ap = h < 12 ? 'a' : 'p';
  const hh = (h % 12) || 12;
  return m ? `${hh}:${String(m).padStart(2, '0')}${ap}` : `${hh}${ap}`;
}
// Compact one-line summary for the row, e.g. "⏱ Th,Fr,Sa,Su · 6p–4a".
function scheduleBadge(s) {
  if (!s || !s.enabled || !(s.active_dows || []).length) return null;
  const days = s.active_dows.map((d) => DOW_LABELS[d]).join(',');
  const win = (s.start_time || s.end_time) ? ` · ${ampm(s.start_time)}–${ampm(s.end_time)}` : '';
  return `⏱ ${days}${win}`;
}

// Human-readable summary of a group's filters, e.g. 'URL contains "tixr" OR Name contains "LIV"'.
function summarizeGroup(g) {
  const fs = (g.filters && g.filters.length) ? g.filters : (g.rule_type ? [{ type: g.rule_type, value: g.rule_value }] : []);
  if (!fs.length) return '(no filters)';
  const join = g.match_mode === 'all' ? ' AND ' : ' OR ';
  return fs.map((f) => `${f.type === 'url_contains' ? 'URL' : 'Name'} contains “${f.value}”`).join(join);
}

const emptyFilter = () => ({ type: 'name_contains', value: '' });

// Reusable add/edit form: name + one-or-more filters + AND/OR mode.
function GroupForm({ initial, busy, submitLabel, onSubmit, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [matchMode, setMatchMode] = useState(initial?.match_mode === 'all' ? 'all' : 'any');
  const [filters, setFilters] = useState(
    (initial?.filters && initial.filters.length)
      ? initial.filters.map((f) => ({ type: f.type, value: f.value }))
      : [emptyFilter()],
  );
  const setF = (i, k) => (e) => setFilters((fs) => fs.map((f, j) => (j === i ? { ...f, [k]: e.target.value } : f)));
  const addRow = () => setFilters((fs) => [...fs, emptyFilter()]);
  const rmRow = (i) => setFilters((fs) => (fs.length > 1 ? fs.filter((_, j) => j !== i) : fs));
  const valid = name.trim() && filters.some((f) => f.value.trim());
  const submit = () => onSubmit({
    name: name.trim(),
    match_mode: matchMode,
    filters: filters.filter((f) => f.value.trim()).map((f) => ({ type: f.type, value: f.value.trim() })),
  });

  return (
    <div className="space-y-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name (e.g. Tixr)"
        className="w-full px-2 py-1.5 text-sm rounded-md border border-input bg-background" />
      {filters.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Match</span>
          <div className="inline-flex border border-input rounded-md overflow-hidden">
            <button type="button" onClick={() => setMatchMode('any')} className={`px-2 py-0.5 ${matchMode === 'any' ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>Any (OR)</button>
            <button type="button" onClick={() => setMatchMode('all')} className={`px-2 py-0.5 ${matchMode === 'all' ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>All (AND)</button>
          </div>
          <span>of these filters</span>
        </div>
      )}
      {filters.map((f, i) => (
        <div key={i} className="flex gap-2 items-center">
          <select value={f.type} onChange={setF(i, 'type')} className="px-2 py-1.5 text-sm rounded-md border border-input bg-background">
            <option value="url_contains">URL contains</option>
            <option value="name_contains">Name contains</option>
          </select>
          <input value={f.value} onChange={setF(i, 'value')} placeholder="value (e.g. tixr)" className="flex-1 min-w-0 px-2 py-1.5 text-sm rounded-md border border-input bg-background" />
          <button type="button" onClick={() => rmRow(i)} disabled={filters.length === 1} title="Remove filter" className="px-1.5 text-muted-foreground hover:text-destructive disabled:opacity-30">✕</button>
        </div>
      ))}
      <button type="button" onClick={addRow} className="text-xs text-primary hover:underline">+ add filter</button>
      <div className="flex gap-2 pt-1">
        <button onClick={submit} disabled={busy || !valid} className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50">
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && <button onClick={onCancel} disabled={busy} className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-accent disabled:opacity-50">Cancel</button>}
      </div>
    </div>
  );
}

// Manage custom match groups — each is a name + one-or-more filters (AND/OR).
// Grouped campaigns leave the Unmatched tab into their own tab.
function MatchGroupsModal({ tenantSlug, groups, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [addKey, setAddKey] = useState(0); // bump to reset the add form after a successful create

  const call = async (path, method, payload) => {
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/meta-match-groups${path}`, {
        method,
        headers: payload ? { 'Content-Type': 'application/json' } : undefined,
        body: payload ? JSON.stringify(payload) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || (d && d.ok === false)) throw new Error(d.detail || d.error || `failed (${r.status})`);
      return true;
    } catch (e) { setErr(e.message); return false; } finally { setBusy(false); }
  };

  const create = async (payload) => { if (await call('', 'POST', payload)) { setAddKey((k) => k + 1); onChanged?.(); } };
  const update = async (id, payload) => { if (await call(`/${id}`, 'PATCH', payload)) { setEditingId(null); onChanged?.(); } };
  const remove = async (id) => { if (await call(`/${id}`, 'DELETE')) onChanged?.(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">Match groups</h2>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground px-1">✕</button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Bucket campaigns by one or more filters (e.g. URL contains “tixr”). Grouped campaigns move out of the Unmatched tab into their own tab.</p>

        <div className="space-y-1.5 mb-4">
          {groups.length === 0 && <div className="text-sm text-muted-foreground">No groups yet.</div>}
          {groups.map((g) => (
            editingId === g.id ? (
              <div key={g.id} className="border border-border rounded-md p-2 bg-muted/20">
                <div className="text-xs font-medium text-muted-foreground mb-2">Edit “{g.name}”</div>
                <GroupForm initial={g} busy={busy} submitLabel="Save" onCancel={() => setEditingId(null)} onSubmit={(p) => update(g.id, p)} />
              </div>
            ) : (
              <div key={g.id} className="flex items-center gap-2 text-sm border border-border rounded-md px-2 py-1.5">
                <span className="font-medium">{g.name}</span>
                <span className="text-xs text-muted-foreground flex-1 truncate" title={summarizeGroup(g)}>{summarizeGroup(g)}</span>
                <button onClick={() => setEditingId(g.id)} disabled={busy} className="text-xs text-primary hover:underline disabled:opacity-50">Edit</button>
                <button onClick={() => remove(g.id)} disabled={busy} className="text-xs text-destructive hover:underline disabled:opacity-50">Delete</button>
              </div>
            )
          ))}
        </div>

        <div className="border-t border-border pt-3">
          <div className="text-xs font-medium text-muted-foreground mb-2">Add group</div>
          <GroupForm key={addKey} busy={busy} submitLabel="Add group" onSubmit={create} />
        </div>

        {err && <div className="text-xs text-destructive mt-2 break-words">{err}</div>}
      </div>
    </div>
  );
}

function ScheduleEditor({ campaign, tenantSlug, onSaved }) {
  const s = campaign.schedule;
  const [enabled, setEnabled] = useState(s?.enabled ?? false);
  const [dows, setDows] = useState(new Set(s?.active_dows || []));
  const [start, setStart] = useState(hhmm(s?.start_time));
  const [end, setEnd] = useState(hhmm(s?.end_time));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggleDow = (d) => setDows((prev) => {
    const n = new Set(prev); if (n.has(d)) n.delete(d); else n.add(d); return n;
  });

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/meta-campaigns/${campaign.id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled,
          active_dows: [...dows],
          start_time: start || null,
          end_time: end || null,
        }),
      });
      const d = await res.json();
      if (!res.ok) { setMsg(d.error === 'read_only_mode' ? 'Writes disabled (READ_ONLY_MODE)' : (d.detail || d.error || 'failed')); return; }
      setMsg(`Saved${d.applied?.changed ? ` · now ${d.applied.desired}` : ''}`);
      onSaved?.();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const overnight = start && end && end <= start;
  return (
    <div className="border border-border rounded-md p-3 bg-card text-xs">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-medium text-sm">Schedule</span>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>{enabled ? 'On, auto pause/activate' : 'Off, not managed'}</span>
        </label>
      </div>
      <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
        <div className="flex flex-wrap gap-1 mb-2">
          {DOW_LABELS.map((lbl, d) => (
            <button
              key={d}
              type="button"
              onClick={() => toggleDow(d)}
              className={`px-2 py-1 rounded border ${dows.has(d) ? 'bg-accent text-accent-foreground border-primary font-medium' : 'border-input hover:bg-accent/40'}`}
            >{lbl}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1">On at
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="px-1.5 py-0.5 rounded border border-input bg-background" />
          </label>
          <label className="flex items-center gap-1">Off at
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="px-1.5 py-0.5 rounded border border-input bg-background" />
          </label>
          <span className="text-muted-foreground">{overnight ? '(next morning)' : (!start && !end ? '(all day on selected days)' : '')}</span>
          <span className="text-muted-foreground">· Miami (ET)</span>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="px-3 py-1 bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
        >{busy ? 'Saving…' : 'Save schedule'}</button>
        {msg && <span className="text-muted-foreground">{msg}</span>}
        {s?.last_applied_at && !msg && (
          <span className="text-muted-foreground">last applied {s.last_applied_status} · {fmtEventDate(s.last_applied_at)}</span>
        )}
      </div>
    </div>
  );
}

function CampaignDetail({ campaign, tenantSlug, windowed, onSaved }) {
  // In a windowed view `totals` carries the WINDOW's metrics (from the
  // per-campaign insights fetch); in All time it's the lifetime adset rollup.
  const t = campaign.totals;
  return (
    <div className="space-y-3">
      <EventMatchEditor campaign={campaign} tenantSlug={tenantSlug} onSaved={onSaved} />
      <ScheduleEditor campaign={campaign} tenantSlug={tenantSlug} onSaved={onSaved} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <MiniStat label="Impressions" value={num(t.impressions)} />
        <MiniStat label="Reach"       value={num(t.reach)} />
        <MiniStat label="Clicks"      value={num(t.clicks)} />
        <MiniStat label="CTR"         value={pct(t.ctr)} />
        <MiniStat label="CPM"         value={dollars(Math.round(t.cpm * 100))} />
        <MiniStat label="CPC"         value={dollars(Math.round(t.cpc * 100))} />
        <MiniStat label="Frequency"   value={(t.frequency || 0).toFixed(2)} />
        <MiniStat label="Cost / purchase" value={t.cost_per_purchase_cents > 0 ? dollars(t.cost_per_purchase_cents) : '—'} />
      </div>

      {/* Ad-set breakdown only exists as lifetime snapshots, no windowed source. */}
      {windowed && campaign.adsets && campaign.adsets.length > 0 && (
        <div className="text-[11px] text-muted-foreground italic">Ad-set breakdown shows in the All-time view.</div>
      )}
      {!windowed && campaign.adsets && campaign.adsets.length > 0 && (
        <>
        {/* Mobile: stacked ad-set cards (the table below is md+ only) */}
        <div className="md:hidden space-y-2">
          {campaign.adsets.map((a) => {
            const m = adsetMetrics(a);
            return (
              <div key={a.id} className="border border-border rounded-md p-2 bg-card text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{a.name}</span>
                  <span className={`text-[10px] px-1 rounded flex-shrink-0 ${STATUS_TONE[a.effective_status] || 'bg-muted text-muted-foreground'}`}>
                    {a.effective_status}
                  </span>
                </div>
                <div className="mt-1 grid grid-cols-3 gap-1 tabular-nums text-muted-foreground">
                  <div>Spend: ${m.spend.toFixed(2)}</div>
                  <div>Impr: {m.impressions.toLocaleString()}</div>
                  <div>Clicks: {m.clicks.toLocaleString()}</div>
                  <div>Purch: {m.purchases.toLocaleString()}</div>
                  <div className={`font-medium ${roasTone(m.roas)}`}>ROAS: {roasFmt(m.roas)}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-2 py-1 font-medium">Ad set</th>
                <th className="px-2 py-1 font-medium">Status</th>
                <th className="px-2 py-1 font-medium text-right">Spend</th>
                <th className="px-2 py-1 font-medium text-right">Impr</th>
                <th className="px-2 py-1 font-medium text-right">Clicks</th>
                <th className="px-2 py-1 font-medium text-right">Purch</th>
                <th className="px-2 py-1 font-medium text-right">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {campaign.adsets.map((a) => {
                const m = adsetMetrics(a);
                return (
                  <tr key={a.id} className="border-t border-border">
                    <td className="px-2 py-1 truncate max-w-xs">{a.name}</td>
                    <td className="px-2 py-1">
                      <span className={`text-[10px] px-1 rounded ${STATUS_TONE[a.effective_status] || 'bg-muted text-muted-foreground'}`}>
                        {a.effective_status}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">${m.spend.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{m.impressions.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{m.clicks.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{m.purchases.toLocaleString()}</td>
                    <td className={`px-2 py-1 text-right tabular-nums font-medium ${roasTone(m.roas)}`}>{roasFmt(m.roas)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// Manual campaign→event pinning. Auto-matching (destination URL → name date)
// can't place every campaign; this pins one to a specific event, clears a pin
// (back to auto), or excludes the campaign from matching entirely. DB-only
// (no Meta write) and takes effect on the perf page AND the event dashboard.
function EventMatchEditor({ campaign, tenantSlug, onSaved }) {
  const [picking, setPicking] = useState(false);
  const [events, setEvents] = useState(null); // null = not loaded yet
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async (body) => {
    setBusy(true); setErr(null);
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/meta-campaigns/${campaign.id}/match`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'failed');
      setPicking(false); setQuery('');
      onSaved?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openPicker = () => {
    setErr(null); setPicking(true);
    if (events) return;
    apiFetch(`/api/tenants/${tenantSlug}/events?all=1`)
      .then((r) => r.json())
      .then((b) => {
        if (b.error) throw new Error(b.detail || b.error);
        const list = (b.events || []).map((e) => ({ id: e.id, date: String(e.date).slice(0, 10), talent: e.talent || e.event_name || '' }));
        list.sort((a, x) => x.date.localeCompare(a.date)); // newest first
        setEvents(list);
      })
      .catch((e) => setErr(e.message));
  };

  const q = query.toLowerCase();
  const candidates = (events || [])
    .filter((e) => !q || e.talent.toLowerCase().includes(q) || e.date.includes(q))
    .slice(0, 12);
  const pinned = campaign.match_method === 'manual';

  return (
    <div className="border border-border rounded-md p-2 bg-card text-xs space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Event match</span>
        {campaign.match_disabled ? (
          <span className="text-muted-foreground">Excluded from matching</span>
        ) : campaign.matched_event ? (
          <span>
            {fmtEventDate(campaign.matched_event.date)}
            {campaign.matched_event.talent ? ` · ${campaign.matched_event.talent}` : ''}
            {MATCH_METHOD_BADGE[campaign.match_method] && (
              <span className="ml-1.5 text-[9px] px-1 py-px rounded bg-muted text-muted-foreground" title={MATCH_METHOD_BADGE[campaign.match_method].title}>
                {MATCH_METHOD_BADGE[campaign.match_method].label}
              </span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {campaign.match_method === 'general' ? 'General campaign (not tied to one event)' : 'No event matched'}
          </span>
        )}
        <span className="flex-1" />
        {!picking && (
          <button onClick={openPicker} disabled={busy}
            className="px-2 py-1 border border-input rounded hover:bg-accent disabled:opacity-50">
            {pinned ? 'Re-pin…' : 'Pin to event…'}
          </button>
        )}
        {pinned && (
          <button onClick={() => save({ event_id: null })} disabled={busy}
            className="px-2 py-1 border border-input rounded hover:bg-accent disabled:opacity-50"
            title="Remove the pin and go back to automatic matching">
            Unpin
          </button>
        )}
        {campaign.match_disabled ? (
          <button onClick={() => save({ event_id: null, disabled: false })} disabled={busy}
            className="px-2 py-1 border border-input rounded hover:bg-accent disabled:opacity-50">
            Re-enable matching
          </button>
        ) : (
          <button onClick={() => save({ event_id: null, disabled: true })} disabled={busy}
            className="px-2 py-1 border border-input rounded hover:bg-accent disabled:opacity-50"
            title="This campaign will never attach to an event (spend stays out of per-event totals)">
            Never match
          </button>
        )}
      </div>
      {picking && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search events by talent or date (YYYY-MM-DD)…"
              className="flex-1 px-2 py-1 border border-input rounded bg-background"
            />
            <button onClick={() => { setPicking(false); setQuery(''); }} disabled={busy}
              className="px-2 py-1 border border-input rounded hover:bg-accent disabled:opacity-50">Cancel</button>
          </div>
          {!events && !err && <div className="text-muted-foreground py-1">Loading events…</div>}
          {events && candidates.length === 0 && <div className="text-muted-foreground py-1">No events match.</div>}
          {candidates.map((e) => (
            <button
              key={e.id}
              onClick={() => save({ event_id: e.id })}
              disabled={busy}
              className="w-full text-left px-2 py-1 border border-border rounded hover:bg-accent disabled:opacity-50 flex items-center gap-2"
            >
              <span className="tabular-nums whitespace-nowrap">{e.date}</span>
              <span className="truncate">{e.talent}</span>
            </button>
          ))}
        </div>
      )}
      {err && <div className="text-destructive break-words">{err}</div>}
    </div>
  );
}

function adsetMetrics(a) {
  const ins = a.insights?.data?.[0] || {};
  const spend = parseFloat(ins.spend || '0');
  const purAct = (ins.actions || []).find((x) => x.action_type === 'purchase' || x.action_type === 'offsite_conversion.fb_pixel_purchase');
  const purVal = (ins.action_values || []).find((x) => x.action_type === 'purchase' || x.action_type === 'offsite_conversion.fb_pixel_purchase');
  const purchases = parseInt(purAct?.value || '0', 10);
  const purchaseValue = parseFloat(purVal?.value || '0');
  const roas = spend > 0 ? purchaseValue / spend : 0;
  return {
    spend,
    impressions: parseInt(ins.impressions || '0', 10),
    clicks: parseInt(ins.clicks || '0', 10),
    purchases,
    roas,
  };
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-border rounded-md px-2 py-1.5 bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-semibold tabular-nums">{value}</div>
    </div>
  );
}
